// ABOUTME: The grid tab of the relation Inkoopadvies page — a ShDataGrid3 of stock forecast rows,
// ABOUTME: pre-sorted by restock urgency, with the recommended order quantity as the headline column.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseCard from "../../../../components/theme/cards/BaseCard";
import CardLabel from "../../../../components/theme/label/CardLabel";
import MpAddInbound from "../../../../components/global/ModalsProgression/MpAddInbound/MpAddInbound";
import ShCopyButton from "../../../../components/theme/ShCopy";
import ShDataGrid3 from "../../../../components/theme/datagrid3/ShDataGrid3";
import ShDataGridSelectedActions from "../../../../components/theme/datagrid2/ShDataGridSelectedActions";
import SHQuickAction from "../../../../components/theme/Modal/SHQuickAction";
import ShLabel from "../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import Text from "../../../../components/theme/text/Text";
import TopRight from "../../../../components/theme/positionals/TopRight";
import { formatNumber, formatPrice } from "../../../../utils/helpers";
import { useGetStockForecastQuery } from "../../../../newapi/warehouse/productSlice";
import { tokens } from "../../../../theme";

const SpStockDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));
const SpInboundDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpInboundDetails/SpInboundDetails"));

// Urgency colour for days-until-stockout: red once out, orange within the forecast horizon, green beyond.
const daysUntilStockoutVariant = (days, daysInFuture) => {
    if (days <= 0) return "red";
    if (days < daysInFuture) return "orange";
    return "green";
};

// Risk colour for the historical stock-out percentage.
const stockOutRiskVariant = (risk) => {
    if (risk >= 50) return "red";
    if (risk >= 25) return "orange";
    return "green";
};

// The compact card shown in the phone-column--cell, mirroring InboundsCard's layout.
const PurchaseAdviceCard = (props) => {
    const { t, i18n } = useTranslation();

    if (props.skeleton) {
        return (
            <BaseCard>
                <ShTextSkeleton />
            </BaseCard>
        );
    }

    return (
        <BaseCard onClick={props.onClick}>
            <Box>
                <Box display={"flex"} flexDirection={"column"}>
                    <Text bold>{props.sku}</Text>
                    <Text>{props.description}</Text>
                </Box>
                <TopRight>
                    <CardLabel variant="blue" position="child-tr">
                        {formatNumber(i18n, props.recommendedOrderQuantity)}
                    </CardLabel>
                </TopRight>
                <Box marginTop={3} display={"flex"} justifyContent={"space-between"}>
                    <Box display={"flex"} flexDirection={"column"}>
                        <Text light>{t("available_stock")}</Text>
                        <Text>{formatNumber(i18n, props.currentAvailableStock)}</Text>
                    </Box>
                    <Box display={"flex"} flexDirection={"column"}>
                        <Text light>{t("incoming_stock")}</Text>
                        <Text>{formatNumber(i18n, props.incomingQuantity ?? 0)}</Text>
                    </Box>
                    {props.stockLevel ? (
                        <Box display={"flex"} flexDirection={"column"}>
                            <Text light>{t("backorders")}</Text>
                            <Text>{formatNumber(i18n, props.backorderQuantity ?? 0)}</Text>
                        </Box>
                    ) : (
                    <Box display={"flex"} flexDirection={"column"}>
                        <Text light>{t("days_until_stockout")}</Text>
                        <Text>{props.daysUntilStockout === null ? t("no_sales") : props.daysUntilStockout}</Text>
                    </Box>
                    )}
                </Box>
            </Box>
        </BaseCard>
    );
};

const PurchaseAdvice = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const params = props.params ?? {};

    // The three calculation inputs form the grid query body. ShDataGrid3 spreads this into the
    // request and layers the supplier filter on top, so changing either re-fetches automatically.
    const gridConfig = { ...params };
    const isStockLevel = params.basis === "stocklevel";

    const [spStockLoading, setSpStockLoading] = useState(false);
    const [isExtended, setIsExtended] = useState(false);
    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);

    // The selection lives on the page (index.jsx) so it can outlast a filter change: rows kept from
    // earlier filters plus the rows ticked now, as full row objects that become the inbound lines.
    const { selectedRows, setSelectedRows, rememberedRows, selectedItems, resetSelection } = props;
    const allSelectedRows = [...rememberedRows, ...selectedRows];
    const rememberedBasisById = new Map(rememberedRows.map((row) => [row.id, row.adviceBasis]));
    const [inboundModalOpen, setInboundModalOpen] = useState(false);
    const [inboundInitialData, setInboundInitialData] = useState(null);

    // Only once the inbound exists: it covers exactly the rows that were selected, so they drop out of
    // the advice list and the ticks would otherwise point at rows that are no longer there. Dismissing
    // the modal leaves the selection intact.
    const onInboundCreated = () => {
        resetSelection();
    };

    // "Save and open" from MpAddInbound opens the created inbound in its details sidepanel.
    const [spInboundLoading, setSpInboundLoading] = useState(false);
    const [inboundIsExtended, setInboundIsExtended] = useState(false);
    const [inboundId, setInboundId] = useState();
    const openCreatedInbound = (id) => {
        setInboundId(parseInt(id));
        setSpInboundLoading(true);
        setInboundIsExtended(true);
    };

    // Turn the selected forecast rows into a seeded MpAddInbound: one inbound line per product with
    // the advised quantity, and — when every selection shares one supplier (i.e. the grid is filtered
    // by supplier) — that supplier pre-selected.
    const openCreateInbound = () => {
        const inboundLines = allSelectedRows.map((row) => ({
            isDescriptionRow: false,
            description: "",
            sku: row.sku,
            amountForecasted: row.recommendedOrderQuantity,
            serialNumbers: [],
            batch: "",
            lot: "",
            bbDate: "",
        }));

        const supplierIds = [...new Set(allSelectedRows.map((row) => row.supplierId).filter(Boolean))];
        const supplierId = supplierIds.length === 1 ? supplierIds[0] : undefined;

        // fromPurchaseAdvice adds the step that previews and edits the mail to the supplier.
        setInboundInitialData({ ...(supplierId ? { supplierId } : {}), inboundLines, fromPurchaseAdvice: true });
        setInboundModalOpen(true);
    };

    // Forecast rows key the product as productId — there is no plain id field on them.
    const getProductDetails = (row) => {
        if (!row) return;

        const newProductId = parseInt(row.productId);
        // Only update state if the productId has actually changed
        if (productIdRef.current !== newProductId) {
            if (!spStockLoading) {
                setSpStockLoading(true);
            }
            setProductId(newProductId);
            productIdRef.current = newProductId;
        }
        setIsExtended(true);
    };

    const gridOptions = {
        export: false,                              // no export endpoint for the forecast
        search: true,                               // backend matches SKU or description
        // Supplier is filtered from the topbar (see index.jsx), so no grid filter panel here.
    };

    const columns = [
        {
            field: "sku",
            headerName: t("sku"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box sx={{
                        width: 1,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        ".copyel": {
                            opacity: 0,
                        },
                        "&:hover .copyel": {
                            opacity: 1,
                        }
                    }}>
                        {/* Only the SKU itself opens the sidepanel — the copy button sits outside the
                            click target so copying never also expands the panel. */}
                        <Box onClick={() => getProductDetails(p.row)}>
                            <SHQuickAction
                                disableScrollLock={true}
                                component={
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Text light>{t("sku")}</Text>
                                        <Text>{p.row.sku}</Text>
                                    </Box>
                                }
                            >
                                {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                                    <Box
                                        aria-owns={anchorEl ? "simple-popover" : undefined}
                                        aria-haspopup="true"
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                        onTouchStart={onTouchStart}
                                        sx={{ display: "inline-flex", minWidth: 0 }}
                                    >
                                        <Text clickable semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                                            {p.row.sku}
                                        </Text>
                                    </Box>
                                )}
                            </SHQuickAction>
                        </Box>
                        <ShCopyButton className={"copyel"} closeAfter={1500} value={p.row.sku}></ShCopyButton>
                    </Box>
                );
            },
        },
        {
            field: "description",
            headerName: t("product"),
            flex: 2,
            sortable: false,
            // The column is too narrow for most descriptions, so the hover repeats it in full.
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                if (!p.row.description) return <Text>-</Text>;
                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Text light>{t("product")}</Text>
                                <Text>{p.row.description}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "inline-flex", minWidth: 0 }}
                            >
                                <Text>{p.row.description}</Text>
                            </Box>
                        )}
                    </SHQuickAction>
                );
            },
        },
        {
            field: "supplierName",
            headerName: t("supplier"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                if (!p.row.supplierName) return <Text>-</Text>;
                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Text light>{t("supplier")}</Text>
                                <Text>{p.row.supplierName}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "inline-flex", minWidth: 0 }}
                            >
                                <Text>{p.row.supplierName}</Text>
                            </Box>
                        )}
                    </SHQuickAction>
                );
            },
        },
        {
            field: "currentAvailableStock",
            headerName: t("available_stock"),
            flex: 1,
            sortable: false,
            renderCell: (p) => (p.row.skeleton ? <ShTextSkeleton /> : formatNumber(i18n, p.row.currentAvailableStock)),
        },
        {
            field: "incomingQuantity",
            headerName: t("incoming_stock"),
            flex: 1,
            sortable: false,
            // Announced inbounds plus goods received but awaiting putaway. Both already count towards
            // the advice, so showing the amount keeps a reduced recommendation explainable.
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                if (!p.row.incomingQuantity) return <Text>-</Text>;
                return <Text>{formatNumber(i18n, p.row.incomingQuantity)}</Text>;
            },
        },
        {
            field: "avgDailySales",
            headerName: t("avg_daily_sales"),
            flex: 1,
            sortable: false,
            renderCell: (p) => (p.row.skeleton ? <ShTextSkeleton /> : formatNumber(i18n, p.row.avgDailySales)),
        },
        {
            field: "daysUntilStockout",
            headerName: t("days_until_stockout"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShLabelSkeleton />;

                // No sales in the analysis period leaves nothing to count down from, so the row
                // reads as a neutral grey label instead of an urgency colour.
                let variant = "grey";
                let value = t("no_sales");
                if (theme.palette.mode === "dark") {
                    variant = "greyLight";
                }
                if (p.row.daysUntilStockout !== null) {
                    variant = daysUntilStockoutVariant(p.row.daysUntilStockout, params.daysInFuture);
                    value = p.row.daysUntilStockout;
                }

                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Text light>{t("days_until_stockout")}</Text>
                                <Text>{value}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "inline-flex", minWidth: 0 }}
                            >
                                <ShLabel size={32} palette="normal" variant={variant}>
                                    {value}
                                </ShLabel>
                            </Box>
                        )}
                    </SHQuickAction>
                );
            },
        },
        {
            field: "predictedAvailableStock",
            headerName: t("predicted_stock"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                const negative = p.row.predictedAvailableStock < 0;
                return (
                    <Text sx={negative ? { color: colors.red[400] } : undefined}>
                        {formatNumber(i18n, p.row.predictedAvailableStock)}
                    </Text>
                );
            },
        },
        {
            field: "recommendedOrderQuantity",
            headerName: t("advised_order_quantity"),
            flex: 1,
            sortable: false,
            renderCell: (p) => (p.row.skeleton ? <ShTextSkeleton /> : <Text bold>{formatNumber(i18n, p.row.recommendedOrderQuantity)}</Text>),
        },
        {
            field: "recommendedOrderValue",
            headerName: t("advised_order_value"),
            flex: 1,
            sortable: false,
            // Advised quantity valued at the product's purchase price, so the row shows what the
            // advice costs. Renders as a dash when no purchase price is set — 0,00 would read as free.
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                if (!p.row.purchasePrice) return <Text>-</Text>;
                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Text light>{t("advised_order_value")}</Text>
                                <Text>{formatPrice(i18n, p.row.purchasePrice)}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "inline-flex", minWidth: 0 }}
                            >
                                <Text>{formatPrice(i18n, p.row.recommendedOrderValue)}</Text>
                            </Box>
                        )}
                    </SHQuickAction>
                );
            },
        },
        // {
        //     field: "stockOutRisk",
        //     headerName: t("stockout_risk"),
        //     flex: 1,
        //     sortable: false,
        //     renderCell: (p) => {
        //         if (p.row.skeleton) return <ShLabelSkeleton />;
        //         return (
        //             <ShLabel size={32} palette="normal" variant={stockOutRiskVariant(p.row.stockOutRisk)}>
        //                 {`${formatNumber(i18n, p.row.stockOutRisk)}%`}
        //             </ShLabel>
        //         );
        //     },
        // },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (p) => (
                <PurchaseAdviceCard
                    onClick={() => getProductDetails(p.row)}
                    skeleton={p.row.skeleton ?? false}
                    stockLevel={isStockLevel}
                    {...p.row}
                />
            ),
        },
    ];

    // Stock-level advice has no sales forecast behind it. The figures it is built from — open
    // backorders, the safety stock and the purchase strategy of the product — take the place of the
    // three forecast columns.
    const forecastFields = ["avgDailySales", "daysUntilStockout", "predictedAvailableStock"];
    const stockLevelColumns = [
        {
            field: "backorderQuantity",
            headerName: t("backorders"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                if (!p.row.backorderQuantity) return <Text>-</Text>;
                return <Text sx={{ color: colors.red[400] }}>{formatNumber(i18n, p.row.backorderQuantity)}</Text>;
            },
        },
        {
            field: "safetyStock",
            headerName: t("safety_stock"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShTextSkeleton />;
                // The safety stock only counts for products on the safety stock strategy.
                if (p.row.purchaseStrategy !== "safetystock" || !p.row.safetyStock) return <Text>-</Text>;
                return <Text>{formatNumber(i18n, p.row.safetyStock)}</Text>;
            },
        },
        {
            field: "purchaseStrategy",
            headerName: t("purchase_strategy"),
            flex: 1,
            sortable: false,
            renderCell: (p) => {
                if (p.row.skeleton) return <ShLabelSkeleton />;
                const isBackorders = p.row.purchaseStrategy === "backorders";
                return (
                    <ShLabel size={32} palette="normal" variant={isBackorders ? "orange" : "blue"}>
                        {isBackorders ? t("purchase_strategy_backorders") : t("purchase_strategy_safetystock")}
                    </ShLabel>
                );
            },
        },
    ];
    const gridColumns = !isStockLevel ? columns : columns.flatMap((column) => {
        if (column.field === "avgDailySales") return stockLevelColumns;
        return forecastFields.includes(column.field) ? [] : [column];
    });

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid3
                key={`${theme.palette.mode}-${isStockLevel ? "stocklevel" : "forecast"}`}
                gridKey={isStockLevel ? "relation.purchaseadvice.stocklevel" : "relation.purchaseadvice"}
                content={useGetStockForecastQuery}
                config={gridConfig}
                title={t("purchase_advice")}
                gridOptions={gridOptions}
                columns={gridColumns}
                selectable
                // An inbound is often built from several pages of advice, so select-all only adds or
                // removes the visible page.
                keepSelectionAcrossPages
                selectedItems={selectedItems}
                setSelectedRows={setSelectedRows}
                // Rows kept from an earlier filter are greyed out; the hover names where they were picked.
                isRowSelectable={(row) => !rememberedBasisById.has(row.id)}
                getRowClassName={({ row }) => (rememberedBasisById.has(row.id) ? "disabled" : undefined)}
                getRowTooltip={(row) => {
                    if (!rememberedBasisById.has(row.id)) return undefined;
                    return rememberedBasisById.get(row.id) === "stocklevel"
                        ? t("purchase_advice_already_selected_stock_level")
                        : t("purchase_advice_already_selected_forecast");
                }}
                selectedActions={
                    <ShDataGridSelectedActions showButtons selectedItems={allSelectedRows}>
                        <ButtonBase
                            onClick={openCreateInbound}
                            sx={{
                                background: tokens("light").txt["white"],
                                height: 40,
                                borderRadius: 7,
                                fontSize: "14px",
                                fontFamily: "Roboto, sans-serif",
                                px: 3,
                                boxShadow: "none",
                                border: "none",
                                fontWeight: 500,
                                color: tokens("light").txt["primary"],
                            }}
                        >
                            {t("create_inbound_from_selection")}
                        </ButtonBase>
                    </ShDataGridSelectedActions>
                }
            />

            {spStockLoading &&
                <SpStockDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    productId={productId}
                />
            }

            {spInboundLoading &&
                <SpInboundDetails
                    isExtended={inboundIsExtended}
                    setIsExtended={setInboundIsExtended}
                    inboundId={inboundId}
                />
            }

            <MpAddInbound
                open={inboundModalOpen}
                onOpen={() => setInboundModalOpen(true)}
                onClose={() => setInboundModalOpen(false)}
                handleClose={() => setInboundModalOpen(false)}
                onSaved={onInboundCreated}
                onOpenCreatedInbound={openCreatedInbound}
                initialData={inboundInitialData}
            />
        </Box>
    );
};

export default PurchaseAdvice;
